import { LogEntry } from './LogEntry';

export interface Log {
  uniqueId: string;
  parserMetadata: LogParserMetadata;

  entries: LogEntry[];
  modules: string[];

  foundUserIDs: string[];
  foundOrders: OrderInLog[];
  deviceInfo: DeviceInfo;

  gpsHistory: {
    locations: GpsHistoryLocation[];
  };

  /**
   * When multiple log files are loaded together, this records where each file
   * begins in the merged entries array. The first file always starts at index 0
   * so it is omitted; only indices > 0 are recorded.
   * Each entry: { fileName, firstEntryIndex } where firstEntryIndex is the
   * index into Log.entries of the first entry from that file.
   */
  fileBoundaries: { fileName: string; firstEntryIndex: number }[];

  /**
   * File names that were uploaded but could not be merged because their log
   * format differed from the first (chosen) file's format. Shown to the user
   * as a notice so they know why those files aren't displayed.
   */
  skippedFiles?: string[];
}

export interface OrderInLog {
  orderId: string;
  cityId: string;
  paymentMethod: {
    id: string;
    type: string;
  };
  pickup: Location;
  destinations: Location[];
}

export interface LatLng {
  lat: number;
  lng: number;
}

export interface Location extends LatLng {
  address: string;
}

export interface LogParserMetadata {
  originalFileNames: string[];
  parser: string;
  app: string;
}

export enum PlatformType {
  Android = 'android',
  iOS = 'ios',
  Unknown = 'unknown',
}

export enum AppType {
  RiderAndroid = 'rider-android', // CA.*
  RiderIOS = 'rider-ios', // CI.*
  DriverAndroid = 'driver-android', // DA.*
  DriverIOS = 'driver-ios', // DI.*
  EaterAndroid = 'eater-android', // FA.*
  EaterIOS = 'eater-ios', // FI.*
  Unknown = 'unknown',
}

export function parseAppType(appVersion?: string): AppType {
  if (!appVersion) {
    return AppType.Unknown;
  }
  if (appVersion.startsWith('CA.')) {
    return AppType.RiderAndroid;
  }
  if (appVersion.startsWith('CI.')) {
    return AppType.RiderIOS;
  }
  if (appVersion.startsWith('DA.')) {
    return AppType.DriverAndroid;
  }
  if (appVersion.startsWith('DI.')) {
    return AppType.DriverIOS;
  }
  if (appVersion.startsWith('FA.')) {
    return AppType.EaterAndroid;
  }
  if (appVersion.startsWith('FI.')) {
    return AppType.EaterIOS;
  }
  return AppType.Unknown;
}

export function isDriverApp(appType: AppType): boolean {
  return appType === AppType.DriverAndroid || appType === AppType.DriverIOS;
}

export function isRiderApp(appType: AppType): boolean {
  return appType === AppType.RiderAndroid || appType === AppType.RiderIOS;
}

export function isEaterApp(appType: AppType): boolean {
  return appType === AppType.EaterAndroid || appType === AppType.EaterIOS;
}

export interface DeviceInfo {
  platformType?: PlatformType;
  appType?: AppType;
  deviceType?: string; // raw value: 'android' | 'iphone'
  deviceName?: string; // e.g. 'samsungSM-S911B' or 'iPhone15,2'
  deviceOsVersion?: string; // e.g. '14' or 'iOS26.2'
  appVersion?: string; // e.g. 'CA.192.0' or 'CI.218.0'
}

export interface GpsHistoryLocation extends LatLng {
  logEntryIndex: number;
  timeMs: number;
}
